import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useJsonLd, getSiteUrl } from "@/hooks/useJsonLd";

export default function ArticleOszustwa() {
  const navigate = useNavigate();
  const siteUrl = getSiteUrl();
  useEffect(() => { window.scrollTo(0, 0); document.title = "Jak Uniknąć Oszustw na Kakobuy – 10 Zasad | Kakobuy Spreadsheet"; }, []);

  useJsonLd({
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Jak Uniknąć Oszustw na Kakobuy – 10 Najważniejszych Zasad",
    "description": "10 sprawdzonych zasad bezpiecznych zakupów — chroń swoje pieniądze podczas zamawiania przez Kakobuy Spreadsheet.",
    "url": `${siteUrl}/artykul/jak-uniknac-oszustw-kakobuy`,
    "datePublished": "2026-06-21",
    "dateModified": "2026-06-21",
    "inLanguage": "pl",
    "author": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet Polska",
      "url": siteUrl,
    },
    "publisher": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet Polska",
      "url": siteUrl,
    },
    "image": "https://readdy.ai/api/search-image?query=Magnifying%20glass%20over%20product%20images%20on%20light%20blush%20pink%20surface%2C%20soft%20studio%20lighting%2C%20editorial%20photography%2C%20fraud%20prevention%20concept%2C%20minimal%20composition%2C%20clean%20professional%20look&width=1600&height=900&seq=kako-art-07-hero&orientation=landscape",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/artykul/jak-uniknac-oszustw-kakobuy`,
    },
  });

  return (
    <>
      <Navbar />
      <main>
        <section className="relative min-h-[55vh] flex flex-col overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(https://readdy.ai/api/search-image?query=Magnifying%20glass%20over%20product%20images%20on%20light%20blush%20pink%20surface%2C%20soft%20studio%20lighting%2C%20editorial%20photography%2C%20fraud%20prevention%20concept%2C%20minimal%20composition%2C%20clean%20professional%20look&width=1600&height=900&seq=kako-art-07-hero&orientation=landscape)" }}></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/35"></div>
          <div className="relative z-10 flex-1 flex items-center pt-24 pb-12">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <span className="inline-block px-4 py-1.5 bg-background-50/60 backdrop-blur-md rounded-full text-xs font-semibold text-foreground-800 mb-6 whitespace-nowrap">Blog &gt; Bezpieczeństwo</span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight">Jak Uniknąć Oszustw<br />na Kakobuy?</h1>
              <p className="text-base text-background-50/85 mt-4 max-w-2xl">10 sprawdzonych zasad bezpiecznych zakupów — chroń swoje pieniądze podczas zamawiania przez Kakobuy Spreadsheet.</p>
            </div>
          </div>
        </section>

        <article className="py-16 md:py-24 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="prose prose-lg max-w-none">
              <section className="mb-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">10 zasad bezpiecznych zakupów</h2>
                <div className="space-y-3">
                  {["Zawsze sprawdzaj ocenę sprzedawcy — minimum 4,5 gwiazdki", "Czytaj opinie innych kupujących, szczególnie te z Polski", "Nie zamawiaj od sprzedawców bez historii transakcji", "Korzystaj z ochrony płatności oferowanej przez Kakobuy", "Sprawdzaj zdjęcia produktu — unikaj ofert tylko z grafikami producenta", "Porównuj ceny z kilkoma sprzedawcami przed zakupem", "Nie podawaj danych karty poza platformą Kakobuy", "Zachowaj potwierdzenia zamówienia i korespondencję", "Zgłaszaj podejrzane oferty do obsługi klienta Kakobuy", "Korzystaj z arkusza Kakobuy Spreadsheet — zawiera tylko sprawdzone linki"].map((tip, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 bg-background-100 rounded-xl">
                      <div className="w-7 h-7 rounded-full bg-accent-500 text-background-50 flex items-center justify-center flex-shrink-0 text-xs font-bold">{i + 1}</div>
                      <p className="text-sm text-foreground-700">{tip}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="bg-primary-50 rounded-2xl p-6 md:p-8 border border-primary-200/50 mb-14">
                <h3 className="font-heading text-xl font-bold text-foreground-900 mb-3">Dlaczego arkusz jest bezpieczniejszy?</h3>
                <p className="text-sm text-foreground-700 leading-relaxed"><strong>Kakobuy Spreadsheet</strong> zawiera tylko linki do zweryfikowanych sprzedawców z wysokimi ocenami. Każdy link jest sprawdzany ręcznie przed dodaniem do arkusza, co minimalizuje ryzyko trafienia na oszusta. To najbezpieczniejszy sposób na zakupy z Chin.</p>
              </section>

              <section className="text-center py-10">
                <p className="text-lg text-foreground-700 mb-6">Korzystaj bezpiecznie z <strong>Kakobuy Spreadsheet</strong>.</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="bg-foreground-900 text-background-50 px-7 py-3 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer">Sprawdź Kakobuy Spreadsheet</a>
                  <a href="/" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer" onClick={(e) => { e.preventDefault(); navigate("/"); }}><i className="ri-arrow-left-line mr-1"></i>Strona główna</a>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}